import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { FormControl, FormHelperText, Select } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'

import {
  isDate,
  getYear,
  getMonth,
  getDate,
  format,
  lastDayOfMonth,
} from 'date-fns'
import { DateString } from 'config/format'
import { jstNewDate } from 'ui/utils/jstDatetime'
import getDateLocale from 'ui/utils/getDateLocale'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'block',
    padding: theme.spacing(0),
  },
  select: {
    minWidth: 115,
    marginRight: theme.spacing(3),
  },
}))
export type SelectProps = {
  onChange?: (date: string) => void
  error?: boolean
  helperText?: React.ReactNode
  defaultDate: Date
}

const serialNumber = (start: number, end: number) => {
  return Array.from(Array(end - start + 1), (_, i) => i + start)
}

const SelectDate: React.FC<SelectProps> = ({
  onChange,
  error,
  helperText,
  defaultDate,
}: SelectProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const dateLocale = getDateLocale()
  const years = serialNumber(1900, getYear(jstNewDate))
  const months = serialNumber(0, 11)

  const today = jstNewDate

  const [year, setYear] = useState<number>(getYear(defaultDate || today))
  const [month, setMonth] = useState<number>(getMonth(defaultDate || today))
  const [date, setDate] = useState<number>(getDate(defaultDate || today))
  const [dates, setDates] = useState<Array<number>>(
    serialNumber(1, getDate(lastDayOfMonth(new Date(year, month, 1))))
  )

  const yearHandler = (event: SelectChangeEvent) => {
    const v = Number(event.target.value)
    const ymd = new Date(v, month, date)
    isDate(ymd) && setYear(v)
  }

  const monthHandler = (event: SelectChangeEvent) => {
    const v = Number(event.target.value)
    const ymd = new Date(year, v, date)
    isDate(ymd) && setMonth(v)
  }

  const dateHandler = (event: SelectChangeEvent) => {
    setDates(serialNumber(1, getDate(lastDayOfMonth(new Date(year, month, 1)))))
    const v = Number(event.target.value)
    const ymd = new Date(year, month, v)
    isDate(ymd) && setDate(v)
  }
  useEffect(() => {
    setDates(serialNumber(1, getDate(lastDayOfMonth(new Date(year, month, 1)))))
    onChange && onChange(format(new Date(year, month, date), DateString))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, date])

  return (
    <div className={classes.root}>
      <FormControl variant="outlined" error={error}>
        <Select
          native
          value={String(year)}
          className={classes.select}
          onChange={yearHandler}
        >
          {years.map((y) => (
            <option key={`year${y}`} value={y}>
              {t('{{y}}年', { y })}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="outlined" error={error}>
        <Select
          native
          value={String(month)}
          className={classes.select}
          onChange={monthHandler}
        >
          {months.map((m) => (
            <option key={`month${m}`} value={m}>
              {dateLocale.localize?.month(m)}
            </option>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="outlined" error={error}>
        <Select
          native
          value={String(date)}
          className={classes.select}
          onChange={dateHandler}
        >
          {dates.map((d) => (
            <option key={`date${d}`} value={d}>
              {t('{{d}}日', { d })}
            </option>
          ))}
        </Select>
      </FormControl>
      {helperText && (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      )}
    </div>
  )
}

SelectDate.defaultProps = {
  defaultDate: new Date(1900, 0, 1),
}

export default SelectDate
